// Adyvance Wallet purchase popups (BILLING-001, D-064), compact like the Create order dialog, each
// paid with <PayFromWallet>: a plan period (with credit for the unused days and auto-renew), root AI
// credit packs, premium themes and add-ons. Plans and add-ons are sensitive (step-up).
import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type PlanPeriod, type WalletPlanOffer, type WalletPlans } from "@ady/contracts";
import { formatDate, formatMoney } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import { Button, cx, Icon, Popup, Switch, useToast, type IconName } from "@ady/ui";
import { ApiError } from "../api/client";
import { errorMessage } from "../api/errors";
import { useIdempotencyKeys } from "../api/idempotency";
import { stepUpErrorMessage, useStepUp } from "../auth/StepUp";
import { Section } from "../orders/NewOrderDialog";
import { PayFromWallet } from "./PayFromWallet";
import {
  adyWalletApi,
  refreshWallet,
  topupFor,
  useAddonOffers,
  useAiCredits,
  usePremiumThemes,
  walletShortOf,
} from "./walletApi";
import { useWalletTopup } from "./WalletTopup";

const money = (p: number) => formatMoney(p);
const n = (v: number) => v.toLocaleString("en-IN");

export function PopHeader({
  icon,
  title,
  lead,
  onClose,
}: {
  icon: IconName;
  title: string;
  lead: string;
  onClose: () => void;
}) {
  return (
    <header className="flex items-center gap-2 border-b border-subtle pb-2.5">
      <span className="flex size-7 flex-none items-center justify-center rounded-xs ady-bg-brand text-on-primary">
        <Icon name={icon} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="text-2xs text-fg-tertiary">{lead}</span>
      </span>
      <button
        type="button"
        onClick={onClose}
        className="inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover"
      >
        <Icon name="x" />
        <span className="ady-sr-only">{t("adyWallet.topup.close")}</span>
      </button>
    </header>
  );
}

/** Two columns: the choice on the left, the sunken pay column on the right. */
function Frame({ left, right }: { left: ReactNode; right: ReactNode }) {
  return (
    <div className="grid gap-3 pt-3 lg:grid-cols-[minmax(0,1fr)_15rem]">
      <div className="flex min-w-0 flex-col gap-2.5">{left}</div>
      <div className="flex flex-col gap-2.5 rounded-sm border border-card bg-sunken p-3">
        {right}
      </div>
    </div>
  );
}

/** Toast + top-up offer for a purchase that the wallet could not cover (a race with another spend). */
function useShortHandler() {
  const toast = useToast();
  const topup = useWalletTopup();
  return (e: unknown) => {
    const short = walletShortOf(e);
    if (short) {
      toast.show({
        tone: "error",
        title: t("adyWallet.topup.forItem", { amount: money(short.short_paisa) }),
      });
      topup.open({ amountPaisa: topupFor(short.short_paisa, 0) });
      return;
    }
    toast.show({ tone: "error", title: stepUpErrorMessage(e) ?? errorMessage(e) });
  };
}

export const limitLine = (key: string, value: number): string | null => {
  if (
    ![
      "products",
      "staff",
      "stores",
      "orders_month",
      "sms_month",
      "ai_credits_month",
      "locations",
      "pos_registers",
      "automations",
    ].includes(key)
  )
    return null;
  if (value === 0) return null;
  if (value < 0)
    return t("adyWallet.plan.limits.unlimited", {
      what: i18n.tDynamic(`billing.quotas.${key}`, "billing.quotas.unknown").toLowerCase(),
    });
  return i18n.tDynamic(
    `adyWallet.plan.limits.${key}${value === 1 ? "_one" : ""}`,
    "billing.quotas.unknown",
    { n: n(value) },
  );
};

// ---------- plan checkout ----------

export function CheckoutPopup({
  orgId,
  plans,
  plan,
  period,
  onClose,
}: {
  orgId: string;
  plans: WalletPlans;
  plan: WalletPlanOffer | null;
  period: PlanPeriod;
  onClose: () => void;
}) {
  return (
    <Popup open={Boolean(plan)} onClose={onClose} label={t("adyWallet.plan.choose")} size="lg">
      {plan ? (
        <CheckoutBody orgId={orgId} plans={plans} plan={plan} initial={period} onClose={onClose} />
      ) : null}
    </Popup>
  );
}

function CheckoutBody({
  orgId,
  plans,
  plan,
  initial,
  onClose,
}: {
  orgId: string;
  plans: WalletPlans;
  plan: WalletPlanOffer;
  initial: PlanPeriod;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useIdempotencyKeys();
  const stepUp = useStepUp();
  const onError = useShortHandler();
  const [period, setPeriod] = useState<PlanPeriod>(
    plan.prices.some((p) => p.period === initial) ? initial : (plan.prices[0]?.period ?? "monthly"),
  );
  const [auto, setAuto] = useState(plans.current.auto_renew || plans.current.kind !== "paid");
  const price = plan.prices.find((p) => p.period === period) ?? null;
  const renewing = plan.current && plans.current.kind === "paid";
  const credit = renewing ? 0 : plans.unused_credit_paisa;
  // D-067: a coupon code (checked by POST /billing/quote; a renewal may still have one).
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState<string | null>(null);
  const quote = useQuery({
    queryKey: ["wallet", orgId, "quote", plan.id, period, applied],
    queryFn: () =>
      adyWalletApi.quote(orgId, {
        plan_id: plan.id,
        period,
        ...(applied ? { coupon_code: applied } : {}),
      }),
    enabled: Boolean(price) && (Boolean(applied) || renewing),
    retry: false,
  });
  const couponError =
    applied && quote.error
      ? quote.error instanceof ApiError && typeof quote.error.details?.reason === "string"
        ? i18n.tDynamic(
            `billingOps.coupons.problems.${quote.error.details.reason}`,
            "billingOps.coupons.problems.unknown",
          )
        : errorMessage(quote.error)
      : null;
  const q = quote.data && !couponError ? quote.data : null;
  const total = price ? (q ? q.total_paisa : price.amount_paisa) : null;
  const due = total !== null ? Math.max(0, total - credit) : null;
  const buy = useMutation({
    mutationFn: () => {
      const body = {
        plan_id: plan.id,
        period,
        auto_renew: auto,
        ...(applied && q?.coupon ? { coupon_code: applied } : {}),
      };
      return stepUp(() => adyWalletApi.checkout(orgId, body, keys(body)));
    },
    onSuccess: async (out) => {
      await refreshWallet(qc, orgId);
      toast.show({
        tone: "success",
        title: out.renewed
          ? t("adyWallet.plan.renewed", { date: formatDate(out.ends_at) })
          : t("adyWallet.plan.bought"),
      });
      onClose();
    },
    onError,
  });
  return (
    <div className="flex flex-col">
      <PopHeader
        icon="rocket"
        title={
          renewing
            ? t("adyWallet.plan.renew", { plan: plan.name })
            : t("adyWallet.plan.switchTo", { plan: plan.name })
        }
        lead={plan.summary ?? t("adyWallet.lead")}
        onClose={onClose}
      />
      <Frame
        left={
          <>
            <Section icon="calendar" title={t("adyWallet.plan.period")}>
              <div className="grid grid-cols-3 gap-1.5">
                {plan.prices.map((p) => (
                  <button
                    key={p.period}
                    type="button"
                    aria-pressed={p.period === period}
                    onClick={() => setPeriod(p.period)}
                    className={cx(
                      "relative flex flex-col gap-0.5 rounded-xs border-[1.5px] px-2.5 py-2 text-start",
                      p.period === period
                        ? "ady-outline-brand"
                        : "border-card bg-surface hover:bg-hover",
                    )}
                  >
                    {p.save_pct > 0 ? (
                      <span className="absolute end-1.5 top-1.5 rounded-full bg-success-bg px-1.5 text-2xs font-semibold text-success-fg">
                        {t("adyWallet.plan.save", { pct: p.save_pct })}
                      </span>
                    ) : null}
                    <span className="text-xs font-semibold">
                      {t(`adyWallet.plan.periods.${p.period}`)}
                    </span>
                    <span className="text-sm font-bold tabular-nums">{money(p.amount_paisa)}</span>
                    <span className="text-2xs text-fg-tertiary">
                      {t("adyWallet.plan.perMonth", { amount: money(p.monthly_paisa) })}
                    </span>
                  </button>
                ))}
              </div>
            </Section>
            <Section icon="check" title={t("adyWallet.plan.includes")}>
              <ul className="grid gap-1 text-xs sm:grid-cols-2">
                {/* D-065: the features this plan adds to the shop's current plan. */}
                {plan.entitlements
                  .filter(
                    (e) =>
                      e.kind === "flag" &&
                      e.value !== 0 &&
                      !plans.plans.some(
                        (c) =>
                          c.current && c.entitlements.some((x) => x.key === e.key && x.value !== 0),
                      ),
                  )
                  .map((e) => (
                    <li key={e.key} className="flex items-center gap-1.5 font-medium">
                      <span className="ady-text-brand">
                        <Icon name="plus" />
                      </span>
                      {i18n.tDynamic(`plans.features.${e.key}`, "billing.quotas.unknown")}
                    </li>
                  ))}
                {plan.entitlements
                  .map((e) => (e.kind === "quota" ? limitLine(e.key, e.value) : null))
                  .filter((x): x is string => Boolean(x))
                  .map((line) => (
                    <li key={line} className="flex items-center gap-1.5">
                      <span className="text-success-fg">
                        <Icon name="check" />
                      </span>
                      {line}
                    </li>
                  ))}
              </ul>
            </Section>
            <div className="rounded-xs border border-card bg-surface px-3">
              <Switch
                label={t("adyWallet.plan.autoRenew")}
                description={t("adyWallet.plan.autoRenewBody", {
                  amount: price ? money(price.amount_paisa) : "",
                })}
                checked={auto}
                onCheckedChange={setAuto}
              />
            </div>
          </>
        }
        right={
          <>
            {credit > 0 && price ? (
              <p className="rounded-xs bg-success-bg px-2.5 py-2 text-2xs text-success-fg">
                {t("adyWallet.plan.credit", {
                  amount: money(Math.min(credit, price.amount_paisa)),
                })}
              </p>
            ) : null}
            <div
              className="flex flex-col gap-1 rounded-xs border border-card bg-surface p-2.5"
              data-testid="checkout-coupon"
            >
              <span className="text-xs font-semibold">{t("billingOps.shop.coupon")}</span>
              {q?.coupon ? (
                <span className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-semibold text-success-fg">
                    {t("billingOps.shop.applied", {
                      code: q.coupon.code,
                      off:
                        q.coupon.kind === "percent" ? `${q.coupon.value}%` : money(q.coupon.value),
                    })}{" "}
                    <span className="font-normal text-fg-tertiary">
                      {t("billingOps.shop.appliedFor", { count: q.coupon.payments })}
                    </span>
                  </span>
                  {applied ? (
                    <button
                      type="button"
                      className="text-2xs font-medium text-link"
                      onClick={() => (setApplied(null), setCode(""))}
                    >
                      {t("billingOps.shop.remove")}
                    </button>
                  ) : null}
                </span>
              ) : (
                <form
                  className="flex gap-1.5"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (code.trim()) setApplied(code.trim().toUpperCase());
                  }}
                >
                  <input
                    className="min-h-control w-full rounded-xs border border-control bg-surface px-2.5 font-mono text-sm uppercase"
                    value={code}
                    maxLength={24}
                    placeholder={t("billingOps.shop.couponPh")}
                    aria-label={t("billingOps.shop.coupon")}
                    onChange={(e) => (
                      setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "")),
                      setApplied(null)
                    )}
                    data-testid="checkout-coupon-code"
                  />
                  <Button
                    type="submit"
                    variant="secondary"
                    disabled={!code.trim() || quote.isFetching}
                  >
                    {t("billingOps.shop.apply")}
                  </Button>
                </form>
              )}
              {couponError ? <span className="text-2xs text-danger-fg">{couponError}</span> : null}
              {q && q.discount_paisa > 0 ? (
                <dl className="grid grid-cols-2 gap-x-2 pt-1 text-2xs">
                  <dt className="text-fg-tertiary">{t("billingOps.shop.listPrice")}</dt>
                  <dd className="text-end tabular-nums">{money(q.list_paisa)}</dd>
                  <dt className="text-fg-tertiary">{t("billingOps.shop.discount")}</dt>
                  <dd className="text-end text-success-fg tabular-nums">
                    −{money(q.discount_paisa)}
                  </dd>
                  <dt className="font-semibold">{t("billingOps.shop.total")}</dt>
                  <dd className="text-end font-semibold tabular-nums">{money(q.total_paisa)}</dd>
                </dl>
              ) : null}
            </div>
            <PayFromWallet
              amountPaisa={due}
              lines={[
                [t("adyWallet.spend.plan"), plan.name],
                [t("adyWallet.plan.period"), t(`adyWallet.plan.periods.${period}`)],
              ]}
              paying={buy.isPending}
              onPay={() => buy.mutate()}
              online={{
                kind: "plan",
                plan_id: plan.id,
                period,
                auto_renew: auto,
                ...(applied && q?.coupon ? { coupon_code: applied } : {}),
              }}
            />
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("adyWallet.topup.close")}
            </Button>
          </>
        }
      />
    </div>
  );
}

// ---------- root AI credits ----------

export function AiCreditsPopup({
  orgId,
  open,
  onClose,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Popup open={open} onClose={onClose} label={t("adyWallet.ai.title")} size="lg">
      {open ? <AiBody orgId={orgId} onClose={onClose} /> : null}
    </Popup>
  );
}

function AiBody({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useIdempotencyKeys();
  const onError = useShortHandler();
  const ai = useAiCredits(orgId);
  const packs = ai.data?.packs ?? [];
  const per = (p: { credits: number; price_paisa: number }) => p.price_paisa / p.credits;
  const best = packs.length > 1 ? packs.reduce((a, b) => (per(b) < per(a) ? b : a)) : null;
  const [packId, setPackId] = useState<string | null>(null);
  const pack = packs.find((p) => p.id === (packId ?? packs[0]?.id)) ?? null;
  const buy = useMutation({
    mutationFn: () => adyWalletApi.buyAi(orgId, pack!.id, keys({ pack: pack!.id })),
    onSuccess: async () => {
      await Promise.all([refreshWallet(qc, orgId), ai.refetch()]);
      toast.show({ tone: "success", title: t("adyWallet.ai.bought") });
      onClose();
    },
    onError,
  });
  const m = ai.data?.month;
  return (
    <div className="flex flex-col">
      <PopHeader
        icon="sparkle"
        title={t("adyWallet.ai.title")}
        lead={t("adyWallet.ai.lead")}
        onClose={onClose}
      />
      <Frame
        left={
          <>
            <div className="grid grid-cols-2 gap-1.5 text-xs">
              <span className="rounded-xs border border-card bg-surface px-2.5 py-2">
                {m && m.limit
                  ? t("adyWallet.ai.month", { used: n(m.used), limit: n(m.limit) })
                  : t("adyWallet.ai.monthNone")}
              </span>
              <span className="rounded-xs border border-card bg-surface px-2.5 py-2 font-semibold">
                {t("adyWallet.ai.extra", { count: n(ai.data?.balance ?? 0) })}
              </span>
            </div>
            <Section icon="coins" title={t("adyWallet.ai.packs")}>
              {packs.length === 0 ? (
                <p className="rounded-xs border border-dashed border-control px-3 py-4 text-center text-xs text-fg-tertiary">
                  {ai.isPending ? "…" : t("adyWallet.ai.noPacks")}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                  {packs.map((p) => {
                    const on = p.id === pack?.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        aria-pressed={on}
                        onClick={() => setPackId(p.id)}
                        className={cx(
                          "relative flex flex-col gap-0.5 rounded-xs border-[1.5px] px-2.5 py-2 text-start",
                          on ? "ady-outline-brand" : "border-card bg-surface hover:bg-hover",
                        )}
                      >
                        {best?.id === p.id ? (
                          <span className="absolute end-1.5 top-1.5 rounded-full ady-bg-brand px-1.5 text-2xs font-semibold text-on-primary">
                            {t("adyWallet.ai.best")}
                          </span>
                        ) : null}
                        <span className="text-sm font-bold tabular-nums">
                          {t("adyWallet.ai.credits", { count: n(p.credits) })}
                        </span>
                        <span className="text-xs font-semibold text-link">
                          {money(p.price_paisa)}
                        </span>
                        <span className="text-2xs text-fg-tertiary">
                          {t("adyWallet.ai.perCredit", { price: money(Math.round(per(p))) })}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>
          </>
        }
        right={
          <>
            <PayFromWallet
              amountPaisa={pack?.price_paisa ?? null}
              lines={
                pack
                  ? [
                      [
                        t("adyWallet.ai.packs"),
                        t("adyWallet.ai.credits", { count: n(pack.credits) }),
                      ],
                    ]
                  : []
              }
              paying={buy.isPending}
              disabled={!pack}
              onPay={() => buy.mutate()}
              online={pack ? { kind: "ai_pack", pack_id: pack.id } : null}
            />
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("adyWallet.topup.close")}
            </Button>
          </>
        }
      />
    </div>
  );
}

// ---------- premium themes ----------

export function ThemesPopup({
  orgId,
  open,
  onClose,
  initialKey = null,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  initialKey?: string | null;
}) {
  return (
    <Popup open={open} onClose={onClose} label={t("adyWallet.themes.title")} size="lg">
      {open ? <ThemesBody orgId={orgId} onClose={onClose} initialKey={initialKey} /> : null}
    </Popup>
  );
}

function ThemesBody({
  orgId,
  onClose,
  initialKey,
}: {
  orgId: string;
  onClose: () => void;
  initialKey: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useIdempotencyKeys();
  const onError = useShortHandler();
  const themes = usePremiumThemes(orgId);
  const list = themes.data?.data ?? [];
  const [key, setKey] = useState<string | null>(initialKey);
  const theme =
    list.find((x) => x.key === (key ?? list.find((l) => !l.owned)?.key ?? list[0]?.key)) ?? null;
  const buy = useMutation({
    mutationFn: () => adyWalletApi.buyTheme(orgId, theme!.key, keys({ theme: theme!.key })),
    onSuccess: async () => {
      await Promise.all([refreshWallet(qc, orgId), themes.refetch()]);
      toast.show({ tone: "success", title: t("adyWallet.themes.bought") });
    },
    onError,
  });
  return (
    <div className="flex flex-col">
      <PopHeader
        icon="palette"
        title={t("adyWallet.themes.title")}
        lead={t("adyWallet.themes.lead")}
        onClose={onClose}
      />
      <Frame
        left={
          list.length === 0 ? (
            <p className="rounded-xs border border-dashed border-control px-3 py-6 text-center text-xs text-fg-tertiary">
              {themes.isPending ? "…" : t("adyWallet.themes.noneBody")}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1.5">
              {list.map((x) => {
                const on = x.key === theme?.key;
                return (
                  <button
                    key={x.key}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setKey(x.key)}
                    className={cx(
                      "flex flex-col gap-1 rounded-xs border-[1.5px] p-2.5 text-start",
                      on ? "ady-outline-brand" : "border-card bg-surface hover:bg-hover",
                    )}
                  >
                    <span className="flex h-14 items-center justify-center rounded-xs bg-sunken text-link">
                      <Icon name="layout" gradient />
                    </span>
                    <span className="flex items-center justify-between gap-1 text-sm font-semibold">
                      {x.name}
                      {x.owned ? (
                        <span className="rounded-full bg-success-bg px-1.5 text-2xs font-semibold text-success-fg">
                          {t("adyWallet.themes.owned")}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs text-fg-secondary">
                      {money(x.price_paisa)} ·{" "}
                      {x.period === "yearly"
                        ? t("adyWallet.themes.yearly")
                        : t("adyWallet.themes.once")}
                    </span>
                    {x.owned && x.ends_at ? (
                      <span className="text-2xs text-fg-tertiary">
                        {t("adyWallet.themes.ownedUntil", { date: formatDate(x.ends_at) })}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )
        }
        right={
          <>
            <PayFromWallet
              amountPaisa={
                theme && !(theme.owned && theme.period === "once") ? theme.price_paisa : null
              }
              lines={theme ? [[t("adyWallet.spend.themes"), theme.name]] : []}
              paying={buy.isPending}
              onPay={() => buy.mutate()}
              online={theme ? { kind: "theme", theme_key: theme.key } : null}
              payLabel={theme?.owned ? t("adyWallet.themes.extend") : undefined}
            />
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("adyWallet.topup.close")}
            </Button>
          </>
        }
      />
    </div>
  );
}

// ---------- add-ons ----------

export function AddonsPopup({
  orgId,
  open,
  onClose,
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Popup open={open} onClose={onClose} label={t("adyWallet.addons.title")} size="lg">
      {open ? <AddonsBody orgId={orgId} onClose={onClose} /> : null}
    </Popup>
  );
}

function AddonsBody({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useIdempotencyKeys();
  const stepUp = useStepUp();
  const onError = useShortHandler();
  const offers = useAddonOffers(orgId);
  const list = (offers.data?.offers ?? []).filter((o) => o.monthly_paisa);
  const [key, setKey] = useState<string | null>(null);
  const [qty, setQty] = useState(1);
  const offer = list.find((o) => o.key === (key ?? list[0]?.key)) ?? null;
  const buy = useMutation({
    mutationFn: () => {
      const body = { offer_key: offer!.key, quantity: qty };
      return stepUp(() => adyWalletApi.buyAddon(orgId, body, keys(body)));
    },
    onSuccess: async (out) => {
      await refreshWallet(qc, orgId);
      toast.show({
        tone: "success",
        title: t("adyWallet.addons.bought", { date: formatDate(out.ends_at) }),
      });
      onClose();
    },
    onError,
  });
  const name = (k: string) =>
    i18n.tDynamic(`adyWallet.addons.offers.${k}`, "wallet.addons.offers.unknown");
  return (
    <div className="flex flex-col">
      <PopHeader
        icon="layers"
        title={t("adyWallet.addons.title")}
        lead={t("adyWallet.addons.lead")}
        onClose={onClose}
      />
      <Frame
        left={
          list.length === 0 ? (
            <p className="rounded-xs border border-dashed border-control px-3 py-6 text-center text-xs text-fg-tertiary">
              {offers.isPending ? "…" : t("adyWallet.addons.none")}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-1.5">
                {list.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    aria-pressed={o.key === offer?.key}
                    onClick={() => setKey(o.key)}
                    className={cx(
                      "flex flex-col gap-0.5 rounded-xs border-[1.5px] px-2.5 py-2 text-start",
                      o.key === offer?.key
                        ? "ady-outline-brand"
                        : "border-card bg-surface hover:bg-hover",
                    )}
                  >
                    <span className="text-sm font-semibold">{name(o.key)}</span>
                    <span className="text-xs text-link">
                      {t("adyWallet.addons.month", { price: money(o.monthly_paisa ?? 0) })}
                    </span>
                  </button>
                ))}
              </div>
              <Section icon="plus" title={t("adyWallet.addons.quantity")}>
                <span className="inline-flex w-fit items-center rounded-xs border border-control bg-surface">
                  <button
                    type="button"
                    className="flex min-h-control w-9 items-center justify-center hover:bg-hover"
                    onClick={() => setQty((q) => Math.max(1, q - 1))}
                  >
                    <Icon name="minus" />
                    <span className="ady-sr-only">−</span>
                  </button>
                  <span className="w-8 text-center text-sm font-semibold tabular-nums">{qty}</span>
                  <button
                    type="button"
                    className="flex min-h-control w-9 items-center justify-center hover:bg-hover"
                    onClick={() => setQty((q) => Math.min(20, q + 1))}
                  >
                    <Icon name="plus" />
                    <span className="ady-sr-only">+</span>
                  </button>
                </span>
              </Section>
            </>
          )
        }
        right={
          <>
            <PayFromWallet
              amountPaisa={offer?.monthly_paisa ? offer.monthly_paisa * qty : null}
              lines={offer ? [[name(offer.key), `× ${qty}`]] : []}
              paying={buy.isPending}
              onPay={() => buy.mutate()}
              online={offer ? { kind: "addon", offer_key: offer.key, quantity: qty } : null}
            />
            <Button variant="secondary" fullWidth onClick={onClose}>
              {t("adyWallet.topup.close")}
            </Button>
          </>
        }
      />
    </div>
  );
}
